import { useTranslation } from 'next-i18next';
import React, { ReactElement } from 'react';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { FdrAdventureTravel } from '@hotelplan/supergraph-api';
import { useFdrGclid } from 'components/domain/fdr-jotform';
import { useFdrCo2FootprintAdditionalInfoQuery } from 'schemas/query/product/fdr-product-co2-footprint-additional-info.generated';
import { FdrCo2AdditionalInfo } from './fdr-co2-additional-info';
import {
  FdrCO2FootprintContact,
  FdrCO2FootprintModalDesktop,
  FdrCO2ModalMainText,
} from './fdr-co2-footprint.styles';
import { IFdrCo2FootprintModalProps } from './fdr-co2-footprint.types';
import { getFdrCo2FootprintJotformLink } from './fdr-co2-footprint.utils';

export function FdrCo2FootprintModalDesktop({
  isOpened,
  onClose,
  requestButton,
}: IFdrCo2FootprintModalProps): ReactElement {
  const { id } = useObjectId();
  const [t, { language }] = useTranslation('pdp');
  const { channelType } = useAuthentication();
  const gclid = useFdrGclid();

  const isB2B = channelType === AuthChannelType.B2B;

  const { data, loading } = useFdrCo2FootprintAdditionalInfoQuery({
    skip: !isB2B,
    variables: {
      productId: id,
    },
  });

  const jotformQueryParams = new URL(requestButton?.link?.url || '')
    .searchParams;

  const {
    transportInfo,
    arrivalDeparture,
    overnightStayAccommodation,
    specialities,
    b2bInformationGroups,
  } = (data?.fdrProduct as unknown as FdrAdventureTravel) || {};

  return (
    <FdrCO2FootprintModalDesktop
      show={isOpened}
      onClose={onClose}
      name="footprint-modal"
      title={t('pdp:co2Footprint.moreInfoTitle')}
    >
      <FdrCO2ModalMainText
        dangerouslySetInnerHTML={{
          __html: t(`pdp:co2Footprint.modal.mainTextB2${isB2B ? 'B' : 'C'}`),
        }}
      />
      {loading ? (
        // TODO: add skeleton loader
        <p>{`Loading...`}</p>
      ) : (
        <>
          {isB2B ? (
            <FdrCo2AdditionalInfo
              transportInfo={transportInfo}
              arrivalDeparture={arrivalDeparture}
              duration={
                b2bInformationGroups[0]?.textInformation[0]?.text || null
              }
              overnightStayAccommodation={overnightStayAccommodation}
              specialities={specialities}
            />
          ) : (
            <FdrCO2FootprintContact
              link={getFdrCo2FootprintJotformLink(
                language,
                jotformQueryParams,
                gclid
              )}
            >
              <BsIcon name="info-small-icon" />
              {t('pdp:co2Footprint.modal.contact')}
            </FdrCO2FootprintContact>
          )}
        </>
      )}
    </FdrCO2FootprintModalDesktop>
  );
}
